import styled from 'styled-components';

import { media, BREAKPOINTS } from 'src/styles/breakpoints';

export const ManagementTabs = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ManagementTab = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.primary : theme.grey)};
  background: ${({ theme, $active }) => ($active ? theme.primary90 : theme.background)};
  color: ${({ theme, $active }) => ($active ? theme.primary : theme.dark)};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }
`;

export const DataTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ${media.md} {
    overflow-x: visible;
  }
`;

export const DataTable = styled.table`
  width: 100%;
  min-width: 36rem;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.grey};
    vertical-align: top;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.midGrey};
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.lightGrey};
  }

  tbody tr.skeleton-row:hover {
    background: transparent;
  }

  @media (max-width: ${BREAKPOINTS.lg}px) and (min-width: ${BREAKPOINTS.md + 1}px) {
    min-width: 36rem;
  }

  ${media.md} {
    min-width: 0;
    font-size: 0.8125rem;

    thead {
      display: none;
    }

    tbody tr {
      display: block;
      margin-bottom: 0.75rem;
      padding: 0.75rem 1rem;
      border: 1px solid ${({ theme }) => theme.grey};
      border-radius: 0.5rem;
      background: ${({ theme }) => theme.background};
    }

    tbody tr:hover {
      background: ${({ theme }) => theme.background};
    }

    td {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.65rem 0;
      border-bottom: 1px solid ${({ theme }) => theme.grey};
      text-align: right;

      &:last-child {
        border-bottom: none;
      }

      &:before {
        content: attr(data-label);
        font-weight: 600;
        font-size: 0.75rem;
        color: ${({ theme }) => theme.midGrey};
        text-align: left;
        flex-shrink: 0;
        max-width: 42%;
        text-transform: capitalize;
      }

      > * {
        flex: 1;
        min-width: 0;
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
    }
  }
`;

export const DataTableBody = styled.tbody<{ $dimmed?: boolean }>`
  transition: opacity 0.3s ease;

  ${({ $dimmed }) =>
    $dimmed &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

export const DataTableSkeletonCell = styled.td`
  padding: 0.85rem 1rem;
`;

export const TableActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 0.75rem;

  ${media.md} {
    grid-template-columns: 1fr;
  }
`;

export const PermissionCard = styled.label<{ $checked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme, $checked }) => ($checked ? theme.primary : theme.grey)};
  background: ${({ theme, $checked }) => ($checked ? theme.primary90 : theme.background)};
  cursor: pointer;

  input {
    margin-top: 0.2rem;
    flex-shrink: 0;
  }
`;

export const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  & > div {
    margin-bottom: 0;
  }
`;

export const FieldError = styled.span`
  display: block;
  font-size: 0.75rem;
  line-height: 1.25;
  color: ${({ theme }) => theme.negative};
`;

export const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.midGrey};
  line-height: 1.5;
`;

export const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

export const TablePaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
`;
